import { Rate } from "../queries/kol-payout"

// The campaign.exchange_rates snapshot may arrive as a JSON column value (array)
// or, defensively, as a JSON string. Normalise to Rate[] | null.
export const parseExchangeRatesSnapshot = (raw: any): Rate[] | null => {
  if (raw == null) return null
  let value: any = raw
  if (typeof value === "string") {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!Array.isArray(value)) return null
  const result: Rate[] = []
  for (const item of value) {
    if (item && typeof item.currencyCode === "string" && typeof item.rate === "number") {
      result.push({ currencyCode: item.currencyCode, rate: item.rate })
    }
  }
  return result.length > 0 ? result : null
}

export const getRateFromUSD = (currencyCode: string | null | undefined, rates: Rate[] | null | undefined): number => {
  if (!currencyCode) return 1
  const code = currencyCode.toUpperCase()
  if (code === "USD") return 1
  if (!rates || rates.length === 0) return 1
  const rateObj = rates.find((item) => item.currencyCode?.toUpperCase() === code)
  return rateObj?.rate ?? 1
}

// Resolve USD → target rate, preferring the campaign-pinned snapshot, falling back to live rates.
export const resolveUsdRate = (
  currencyCode: string | null | undefined,
  snapshot: Rate[] | null | undefined,
  fallback: Rate[] | null | undefined,
): number => {
  if (!currencyCode) return 1
  const code = currencyCode.toUpperCase()
  if (code === "USD") return 1
  if (snapshot && snapshot.length > 0) {
    const fromSnap = snapshot.find((r) => r.currencyCode?.toUpperCase() === code)
    if (fromSnap?.rate != null) return fromSnap.rate
  }
  return getRateFromUSD(currencyCode, fallback)
}

// Pull the campaign exchange-rate snapshot off a tranche reference, if any.
export const getTrancheSnapshot = (tranche: any): Rate[] | null => {
  return parseExchangeRatesSnapshot(tranche?.campaign?.exchange_rates)
}

// KA-1936 — display-currency amount ⇄ the USD cents actually stored.
//
// Money is stored platform-wide in USD cents; every reader (merchant Report tab, campaign
// deliverable view) renders it as `(usd_cents / 100) * rate`. An amount typed in RM therefore
// has to be divided by the USD→MYR rate on the way in, or the report inflates it by exactly
// that rate. This mirrors `convertDisplayMinorUnitsToUsdCents` in
// webhooks/src/campaign/convertTrancheBudgetToUsdCents.ts, which does the same for campaign Cash.

/** RM (or any display-currency) amount → USD cents, using the USD→currency rate. */
export const displayAmountToUsdCents = (amount: number, rateFromUsd: number): number => {
  if (!Number.isFinite(amount)) return 0
  const rate = rateFromUsd > 0 ? rateFromUsd : 1
  return Math.round((amount * 100) / rate)
}

/** USD cents → RM (or any display-currency) amount, rounded to `fractionDigits`. */
export const usdCentsToDisplayAmount = (usdCents: number, rateFromUsd: number, fractionDigits = 2): number => {
  if (!Number.isFinite(usdCents)) return 0
  const rate = rateFromUsd > 0 ? rateFromUsd : 1
  const amount = (usdCents / 100) * rate
  const factor = Math.pow(10, fractionDigits)
  return Math.round(amount * factor) / factor
}
